"use client";
import { useTranslations } from "next-intl";
import { useEffect, useCallback } from "react";
import { toastNotify } from "@client/components/ui/toast";
import { useAppSelector } from "@client/store/store.hooks";
import { JoinForm } from "@client/components/meet/JoinForm";
import { LocalUserChoices } from "@livekit/components-react";
import { WaitingRoom } from "@client/components/meet/WaitingRoom";
import { VideoPreview } from "@client/components/meet/VideoPreview";
import { DeviceSelector } from "@client/components/meet/DeviceSelector";
import { usePreJoinMedia } from "@client/components/meet/hooks/usePreJoinMedia";
import { usePreJoinDevices } from "@client/components/meet/hooks/usePreJoinDevices";
import {
  selectUsername,
  selectGuestEmail,
  selectIsGuestMode,
  selectIsJoining,
  selectMediaDefaults,
  selectIsAutoJoining
} from "@client/store/slices/preJoin.slice";
import { Spinner } from "../ui";

/** Props for the PreJoinView component */
export interface PreJoinViewProps {
  onSubmit: (values: LocalUserChoices, shouldPresent?: boolean, guestEmail?: string) => void;
  joinLabel?: string;
  userLabel?: string;
  isWaiting?: boolean;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  hideNameInput?: boolean;
  hidePresentButton?: boolean;
}

/** PreJoinView - Audio/Video setup screen before joining meeting */
export const PreJoinView = ({
  onSubmit,
  joinLabel = "Join Room",
  userLabel = "Your Name",
  isWaiting = false,
  title = "Ready to join?",
  description,
  children,
  hideNameInput = false,
  hidePresentButton = false
}: PreJoinViewProps) => {
  const t = useTranslations();

  // Use translations for defaults if props not provided
  const effectiveJoinLabel = joinLabel === "Join Room" ? t("preJoin.joinNow") : joinLabel;
  const effectiveTitle = title === "Ready to join?" ? t("preJoin.title") : title;
  const effectiveDescription = description || t("preJoin.description");

  // Redux Selectors
  const username = useAppSelector(selectUsername);
  const guestEmail = useAppSelector(selectGuestEmail);
  const isGuestMode = useAppSelector(selectIsGuestMode);
  const isJoining = useAppSelector(selectIsJoining);
  const isAutoJoining = useAppSelector(selectIsAutoJoining);
  const mediaDefaults = useAppSelector(selectMediaDefaults);

  // Device selection hooks - manages available devices and user selections
  // Note: Initial device IDs are undefined until devices are enumerated
  const devices = usePreJoinDevices({
    initialVideoDeviceId: undefined,
    initialAudioDeviceId: undefined
  });

  // Media track hooks - handles actual video/audio streams
  const media = usePreJoinMedia({
    initialVideoEnabled: mediaDefaults.videoEnabled,
    initialAudioEnabled: mediaDefaults.audioEnabled,
    videoDeviceId: devices.selectedVideoDeviceId,
    audioDeviceId: devices.selectedAudioDeviceId
  });

  // Sync mic/camera toggles with Redux defaults if needed, or just let local state handle it.
  // We prioritize local user interaction (media.videoEnabled) over defaults after mount.

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
        e.preventDefault();
        media.toggleAudio();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "e") {
        e.preventDefault();
        media.toggleVideo();
      }
      if (e.key === "Escape") {
        devices.closeMenus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [media, devices]);

  // Form submission
  const handleSubmit = useCallback(
    (shouldPresent: boolean) => {
      if (isJoining) return;

      if (!hideNameInput) {
        if (!username.trim()) {
          toastNotify.error(t("validation.nameRequired"));
          return;
        }
        if (username.length < 3) {
          toastNotify.error(t("validation.nameTooShort"));
          return;
        }
      }

      if (isGuestMode) {
        if (!guestEmail.trim()) {
          toastNotify.error(t("validation.emailRequired"));
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
          toastNotify.error(t("validation.emailInvalid"));
          return;
        }
      }

      onSubmit(
        {
          username,
          videoEnabled: media.videoEnabled,
          audioEnabled: media.audioEnabled,
          videoDeviceId: devices.selectedVideoDeviceId,
          audioDeviceId: devices.selectedAudioDeviceId
        },
        shouldPresent,
        isGuestMode ? guestEmail.trim().toLowerCase() : undefined
      );
    },
    [username, guestEmail, isGuestMode, media, devices, isJoining, hideNameInput, onSubmit, t]
  );
  if (isAutoJoining && isJoining) {
    return (
      <div className="w-full max-w-375 mx-auto px-3 py-2 sm:p-4 md:p-8 flex flex-col xl:flex-row gap-3 sm:gap-6 xl:gap-16 relative min-h-full">
        <div className="flex flex-col items-center justify-center py-12 w-full">
          <Spinner className="w-8 h-8 text-primary" />
          <span className="mt-3 text-sm text-gray-700">{t("preJoin.connecting")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-375 mx-auto px-3 py-2 sm:p-4 md:p-8 flex flex-col xl:flex-row gap-3 sm:gap-6 xl:gap-16 relative min-h-full">
      {/* Left Side: Video Preview */}
      {/* {!isAutoJoining && ( */}
      <div className="w-full xl:w-2/3 max-w-5xl flex flex-col gap-3 sm:gap-6">
        <VideoPreview
          videoTrack={media.videoTrack}
          videoEnabled={media.videoEnabled}
          audioEnabled={media.audioEnabled}
          videoRef={media.videoRef}
          onToggleVideo={media.toggleVideo}
          onToggleAudio={media.toggleAudio}
        />

        {/* Device Settings */}
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center items-center">
          <DeviceSelector
            kind="audio"
            devices={devices.audioDevices}
            selectedDeviceId={devices.selectedAudioDeviceId}
            isEnabled={media.audioEnabled}
            isMenuOpen={devices.isAudioMenuOpen}
            label={devices.getAudioDeviceLabel()}
            onSelect={devices.setSelectedAudioDeviceId}
            onToggleMenu={devices.toggleAudioMenu}
            onCloseMenu={devices.closeMenus}
            zIndex={20}
          />
          <DeviceSelector
            kind="video"
            devices={devices.videoDevices}
            selectedDeviceId={devices.selectedVideoDeviceId}
            isEnabled={media.videoEnabled}
            isMenuOpen={devices.isVideoMenuOpen}
            label={devices.getVideoDeviceLabel()}
            onSelect={devices.setSelectedVideoDeviceId}
            onToggleMenu={devices.toggleVideoMenu}
            onCloseMenu={devices.closeMenus}
            zIndex={10}
          />
        </div>
      </div>
      {/* )} */}

      {/* Right Side: Join Controls or Waiting Room */}
      <div className="w-full xl:w-1/3 flex flex-col justify-start xl:justify-center h-auto xl:h-full py-4 sm:py-4 lg:py-12 max-w-md mx-auto xl:mx-0">
        {isWaiting ? (
          <WaitingRoom />
        ) : (
          <JoinForm
            title={effectiveTitle}
            description={effectiveDescription}
            userLabel={userLabel}
            hideNameInput={hideNameInput}
            joinLabel={effectiveJoinLabel}
            hidePresentButton={hidePresentButton}
            onSubmit={handleSubmit}
          >
            {children}
          </JoinForm>
        )}
      </div>
    </div>
  );
};
